<template>
  <div class="app-layout">
    <StudentNavMenu
      :current-user="currentUser"
      :is-admin="isAdmin"
      :school-context="hasSchoolContext"
      @action="onNavAction"
    />
    <!-- One-time "install on your phone" nudge; renders nothing on desktop,
         when dismissed, or when already installed. -->
    <InstallAppBanner />
    <slot />
    <IssueReportDialog v-if="showIssueReport" @close="showIssueReport = false" />
  </div>
</template>

<script setup>
// Global app shell: mounts the hamburger/drawer on every authenticated page so
// deep pages (testing, schedules, reports, admin) keep the same app menu
// instead of each inventing its own chrome. App.vue swaps this in based on
// route meta: meta.layout === 'bare' opts out (login).
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import StudentNavMenu from '@/components/students/StudentNavMenu.vue'
import IssueReportDialog from '@/components/IssueReportDialog.vue'
import InstallAppBanner from '@/components/pwa/InstallAppBanner.vue'
import { useAuthStore } from '@/store/authStore'
import { ADMIN_ONLY } from '@/config/roles'
import { useGlobalNavActions } from '@/composables/useGlobalNavActions'

const route = useRoute()
const authStore = useAuthStore()

const currentUser = computed(() => authStore.currentUser)
// Mirrors the /admin route guard (same rule DistrictDashboardView used).
const isAdmin = computed(() => ADMIN_ONLY.includes(currentUser.value?.role))
// District-level landers (/district, /my-caseload) declare schoolContext: false —
// school-scoped menu items (aide schedules, gen-ed hours, feedback, testing,
// admin panel) only make sense once the user is working in a school.
const hasSchoolContext = computed(() => route.meta.schoolContext !== false)

const { handleNavAction } = useGlobalNavActions()

// "Report a Problem" opens a dialog here in the shell rather than routing, so
// the report captures the page the user was actually on.
const showIssueReport = ref(false)

function onNavAction(action, payload) {
  if (action === 'report-problem') {
    showIssueReport.value = true
    return
  }
  handleNavAction(action, payload)
}
</script>

<style>
/* Clearance for the fixed hamburger (15px from the corner + a 44px button).
   A centered page already leaves a gap on a wide window, so only add what
   that gap doesn't cover. Half of (viewport − this header) is that right
   margin. Unscoped: header classes live in child views. The admin panel is
   excluded below — its sidebar keeps the right edge
   against the viewport no matter how wide the window is. */
.app-layout .page-header,
.app-layout .header {
  padding-right: max(0px, calc(60px - (100vw - 100%) / 2));
}

.app-layout .admin-layout .page-header,
.app-layout .admin-layout .header {
  padding-right: 60px;
}
</style>
